import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from 'core/store';
import {
  IonAccordion,
  IonAccordionGroup,
  IonBreadcrumb,
  IonBreadcrumbs,
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonPopover,
  IonRow,
  IonSpinner,
  IonText
} from '@ionic/react';
import {
  addOutline,
  chevronDownOutline,
  chevronUpOutline,
  createOutline,
  sparklesOutline,
  trashOutline
} from 'ionicons/icons';
import Image from 'assets/icons/image.svg';
import Documents from 'assets/icons/documents.svg';
import './InventoryDetailsDesktop.scss';
import type { Asset, ProductModelSearchResult } from 'core/types';
import { HKPlatform } from 'core/constants';
import { useHistory, useParams } from 'react-router';
import { getFallbackProductImage, viewDocument } from 'core/util';
import {
  deleteProduct,
  fetchProductDetails,
  requestProductGroup,
  requestProductGroupCount
} from 'features/shared/inventory/store/InventoryActions';
import { saveProductUpdate } from 'features/shared/inventory/utils/saveProductUpdate';
import { buildProductEditPatchBody } from 'features/shared/inventory/utils/buildProductEditPatchBody';
import { ProductGroup } from 'features/shared/inventory/store/InventorySlice';
import InventoryDetailSkeletonLoader from 'features/shared/inventory/components/InventoryDetailSkeletonLoader';
import ProductModelSearchSelect from 'features/shared/inventory/components/ProductModelSearchSelect';
import HKSelect from 'components/hk-select/HKSelect';
import InventoryDeleteConfirmModal from 'features/shared/inventory/components/InventoryDeleteConfirmModal';
import InventoryAnalyzeImageModal from 'features/shared/inventory/components/InventoryAnalyzeImageModal';
import {
  NA_MANUFACTURER_ID,
  useProductCatalogEditFields
} from 'features/shared/inventory/hooks/useProductCatalogEditFields';
import { useProductPhotoActions } from 'features/shared/inventory/hooks/useProductPhotoActions';
import { HKDocumentType } from 'features/shared/documents/DocumentsSlice';
import { displayError, displaySuccess } from 'core/services/AlertActions';
import 'swiper/css';
import 'swiper/css/pagination';
import '@ionic/react/css/ionic-swiper.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

interface ItemAttribute {
  label: string;
  value: string;
}

export interface ItemParams {
  groupId?: string | undefined;
  productId?: string | undefined;
}

const InventoryDetailsDesktop: React.FC = () => {
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const {
    product,
    product_id,
    productGroups,
    productDetailsLoading,
    updateProductLoading
  } = useSelector((state: RootState) => state.inventory);
  const [attributes, setAttributes] = useState<ItemAttribute[]>([]);
  const [group, setGroup] = useState<ProductGroup>({
    id: 0,
    count: 0,
    group: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isAnalyzeModalOpen, setIsAnalyzeModalOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const { addImage, analyzeImage, isAnalyzing } = useProductPhotoActions(
    homeId,
    product,
    () => setIsAnalyzeModalOpen(false)
  );
  // Stable reference (never reassigned) so re-renders from unrelated state
  // (e.g. isActionsOpen) don't fight the accordion's own open/closed state.
  const [openAccordionValues] = useState(['product-details', 'documents']);
  const [editSerial, setEditSerial] = useState('');
  const {
    initialize: initializeCatalogFields,
    groupId: catalogGroupId,
    setGroupId: setCatalogGroupId,
    groupOptions,
    typeId: catalogTypeId,
    setTypeId: setCatalogTypeId,
    typeOptions,
    manufacturerId: catalogManufacturerId,
    setManufacturerId: setCatalogManufacturerId,
    manufacturerOptions,
    model,
    setModel
  } = useProductCatalogEditFields();
  const history = useHistory();
  const dispatch = useDispatch();
  const { groupId, productId: productIdParam } = useParams<ItemParams>();
  const effectiveProductId =
    product_id ?? (productIdParam ? Number(productIdParam) : null);

  const assets = !!product
    ? [...product.product_template.assets, ...product.assets]
    : [];
  const photoAssets = assets.filter(
    (asset) => asset.type === HKDocumentType.Photos
  );
  // Analyze-candidates are the product's OWN photos only — never catalog
  // template stock photos, which won't have this specific unit's serial/
  // model label on them.
  const ownPhotoAssets = (product?.assets || []).filter(
    (asset) => asset.type === HKDocumentType.Photos
  );
  const manualDocuments = assets.filter(
    (asset) => asset.type === HKDocumentType.Manuals
  );

  const displayAssetThumbnail = (asset: Asset) => {
    return asset.thumb_cloud_store ? asset.thumb_cloud_store : asset.file_url;
  };

  const confirmAnalyzeImage = () => {
    if (!ownPhotoAssets.length) {
      dispatch(displayError('Add a photo first, then analyze it.'));
      return;
    }
    setIsAnalyzeModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!!homeId && !!product) {
      // Awaited, and the group list + counts are refetched before navigating:
      // the products list's own fetch effect is keyed on [dispatch, homeId,
      // groupId], none of which change on a goBack(), so without this the
      // just-deleted product stayed visible in the list behind us.
      await dispatch(deleteProduct(homeId, product.id));
      if (!!groupId) {
        dispatch(requestProductGroup(homeId, groupId));
      }
      dispatch(requestProductGroupCount(homeId));
    }
    setIsDeleteConfirmOpen(false);
    history.goBack();
  };

  const startEditing = () => {
    if (!product) return;
    setEditSerial(product.serial || '');
    initializeCatalogFields(product);
    setIsEditing(true);
  };

  const saveEdits = async () => {
    if (!homeId || !product) return;
    const body = buildProductEditPatchBody(
      product,
      editSerial,
      model,
      catalogManufacturerId ?? null
    );
    const { ok, changed } = await saveProductUpdate(
      dispatch,
      homeId,
      product.id,
      body
    );
    // Stay in edit mode on failure so the user's edits survive to be retried —
    // createApiRequest has already shown the real error message.
    if (!ok) return;
    if (changed) {
      dispatch(displaySuccess('Product Updated', 'Success'));
    }
    setIsEditing(false);
  };

  useEffect(() => {
    if (!!product) {
      setAttributes([
        { label: 'Group', value: group.group },
        {
          label: 'Product Type',
          value: product.product_template.type_data.label
        },
        {
          label: 'Brand/Manufacturer',
          value: product.product_template.manufacturer?.name || 'N/A'
        },
        { label: 'Model #', value: product.product_template.model || 'N/A' },
        { label: 'Serial #', value: product.serial || 'N/A' },
        { label: 'Registered', value: product.registered ? 'Yes' : 'No' }
      ]);
    }
  }, [dispatch, product, group]);

  useEffect(() => {
    if (!product && !!homeId && !!effectiveProductId) {
      dispatch(fetchProductDetails(homeId, effectiveProductId));
    }
  }, [dispatch, product, homeId, effectiveProductId]);

  useEffect(() => {
    const goBackToInventoryList = () => {
      history.goBack();
    };
    if (platformType === HKPlatform.DESKTOP && !isDesktopWidth) {
      goBackToInventoryList();
    }
  });

  useEffect(() => {
    if (!!productGroups && !group.count) {
      const g = productGroups?.find((group) => group.id === Number(groupId));
      if (!!g) {
        setGroup(g);
      }
    }
  }, [productGroups, groupId]);

  return (
    <IonPage className="hk-desktop-digital-inventory-details-page">
      <IonRow className="hk-desktop-digital-inventory-details-header">
        <div className="hk-desktop-digital-inventory-details-header-container">
          <IonBreadcrumbs className="hk-desktop-digital-inventory-details-header-breadcrumbs">
            <IonBreadcrumb href="/inventory">Inventory</IonBreadcrumb>
            <IonBreadcrumb onClick={() => history.goBack()}>
              {group.group}
            </IonBreadcrumb>
            <IonBreadcrumb>
              {product?.product_template.manufacturer?.name}{' '}
              {product?.product_template.type_data.label}{' '}
              {product?.product_template.model}
            </IonBreadcrumb>
          </IonBreadcrumbs>
          {!!product && (
            <div className="hk-desktop-digital-inventory-details-header-button">
              {isEditing ? (
                <>
                  <IonButton
                    className="hk-desktop-digital-inventory-details-cancel-button"
                    fill="outline"
                    strong={true}
                    disabled={updateProductLoading}
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </IonButton>
                  <IonButton
                    fill="solid"
                    color="primary"
                    strong={true}
                    disabled={updateProductLoading}
                    onClick={saveEdits}
                  >
                    {updateProductLoading ? (
                      <IonSpinner name="dots" />
                    ) : (
                      'Save Details'
                    )}
                  </IonButton>
                </>
              ) : (
                <>
                  <IonButton
                    id="hk-desktop-inventory-actions-trigger"
                    fill="solid"
                    strong={true}
                    className="hk-desktop-digital-inventory-details-actions-button"
                  >
                    Actions
                    <IonIcon
                      slot="end"
                      icon={chevronDownOutline}
                      className={`hk-desktop-digital-inventory-details-actions-arrow${
                        isActionsOpen
                          ? ' hk-desktop-digital-inventory-details-actions-arrow-open'
                          : ''
                      }`}
                    />
                  </IonButton>
                  <IonPopover
                    trigger="hk-desktop-inventory-actions-trigger"
                    triggerAction="click"
                    dismissOnSelect={true}
                    side="bottom"
                    alignment="end"
                    className="hk-desktop-digital-inventory-details-actions-popover"
                    onIonPopoverWillPresent={() => setIsActionsOpen(true)}
                    onIonPopoverWillDismiss={() => setIsActionsOpen(false)}
                  >
                    <IonList
                      lines="full"
                      className="hk-desktop-digital-inventory-details-actions-list"
                    >
                      <IonItem button detail={false} onClick={startEditing}>
                        <IonIcon slot="start" icon={createOutline} />
                        <IonLabel>Edit Details</IonLabel>
                      </IonItem>
                      <IonItem button detail={false} onClick={addImage}>
                        <IonIcon slot="start" icon={addOutline} />
                        <IonLabel>Add Image</IonLabel>
                      </IonItem>
                      <IonItem
                        button
                        detail={false}
                        onClick={confirmAnalyzeImage}
                      >
                        <IonIcon slot="start" icon={sparklesOutline} />
                        <IonLabel>Analyze Image</IonLabel>
                      </IonItem>
                      <IonItem
                        button
                        detail={false}
                        className="hk-desktop-digital-inventory-details-actions-delete"
                        onClick={() => setIsDeleteConfirmOpen(true)}
                      >
                        <IonIcon slot="start" icon={trashOutline} />
                        <IonLabel>Delete Item</IonLabel>
                      </IonItem>
                    </IonList>
                  </IonPopover>
                </>
              )}
            </div>
          )}
        </div>
      </IonRow>
      <IonContent>
        {productDetailsLoading || !product ? (
          <InventoryDetailSkeletonLoader />
        ) : (
          <div className="hk-desktop-digital-inventory-details-container">
            <div className="hk-desktop-digital-inventory-details">
              <div className="hk-desktop-digital-inventory-details-overview">
                {photoAssets.length ? (
                  <div className="hk-desktop-digital-inventory-details-overview-media">
                    <Swiper
                      className="hk-desktop-digital-inventory-details-overview-image-slider"
                      modules={[Pagination]}
                      pagination={{
                        el: '.hk-desktop-digital-inventory-details-overview-pagination',
                        clickable: true
                      }}
                    >
                      {photoAssets.map((asset) => (
                        <SwiperSlide key={asset.id}>
                          <img
                            src={displayAssetThumbnail(asset)}
                            alt="Product"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="hk-desktop-digital-inventory-details-overview-pagination" />
                  </div>
                ) : (
                  <div className="hk-image-placeholder">
                    <img
                      src={
                        product?.product_template.type
                          ? getFallbackProductImage(
                              product?.product_template.type
                            )
                          : Image
                      }
                      alt="No product"
                    />
                  </div>
                )}
                <div className="hk-desktop-digital-inventory-details-overview-container">
                  <IonAccordionGroup
                    multiple={true}
                    value={openAccordionValues}
                    className="hk-desktop-digital-inventory-details-accordion-group"
                  >
                    <IonAccordion
                      value="product-details"
                      className="hk-desktop-digital-inventory-details-accordion"
                      toggleIcon={chevronUpOutline}
                    >
                      <IonItem
                        slot="header"
                        lines="none"
                        className="hk-desktop-digital-inventory-details-accordion-header"
                      >
                        <IonLabel className="hk-desktop-digital-inventory-details-accordion-title">
                          Product Details
                        </IonLabel>
                      </IonItem>
                      <div
                        slot="content"
                        className="hk-desktop-digital-inventory-details-accordion-content"
                      >
                        {isEditing ? (
                          <div className="hk-desktop-digital-inventory-details-edit-fields">
                            <HKSelect
                              label="Group"
                              value={catalogGroupId}
                              options={groupOptions}
                              onChange={(value) =>
                                setCatalogGroupId(value as number | null)
                              }
                            />
                            <HKSelect
                              label="Product Type"
                              value={catalogTypeId}
                              options={typeOptions}
                              disabled={!catalogGroupId}
                              onChange={(value) =>
                                setCatalogTypeId(value as number | null)
                              }
                            />
                            <HKSelect
                              label="Brand/Manufacturer"
                              value={catalogManufacturerId}
                              options={manufacturerOptions}
                              disabled={!catalogTypeId}
                              onChange={(value) =>
                                setCatalogManufacturerId(value as number | null)
                              }
                            />
                            <div className="hk-inventory-edit-field">
                              <IonText color="medium">Model #</IonText>
                              <ProductModelSearchSelect
                                value={model?.id}
                                valueLabel={model?.label}
                                disabled={
                                  !catalogManufacturerId ||
                                  catalogManufacturerId === NA_MANUFACTURER_ID
                                }
                                typeId={catalogTypeId}
                                manufacturerId={catalogManufacturerId}
                                onChange={(result: ProductModelSearchResult) =>
                                  setModel({
                                    id: result.id,
                                    label: result.model
                                  })
                                }
                              />
                            </div>
                            <div className="hk-inventory-edit-field">
                              <IonText color="medium">Serial #</IonText>
                              <IonInput
                                className="hk-inventory-edit-input"
                                fill="outline"
                                value={editSerial}
                                placeholder="Enter serial number"
                                onIonInput={(e) =>
                                  setEditSerial(e.detail.value || '')
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          attributes.map((att) => {
                            const { label, value } = att;
                            return (
                              <div
                                key={label}
                                className="hk-desktop-digital-inventory-details-overview-attribute"
                              >
                                <IonText className="hk-desktop-digital-inventory-details-overview-attribute-label">
                                  {label}
                                </IonText>
                                <div className="hk-desktop-digital-inventory-details-overview-attribute-value">
                                  {value}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </IonAccordion>
                    {!isEditing && !!manualDocuments.length && (
                      <IonAccordion
                        value="documents"
                        className="hk-desktop-digital-inventory-details-accordion"
                        toggleIcon={chevronUpOutline}
                      >
                        <IonItem
                          slot="header"
                          lines="none"
                          className="hk-desktop-digital-inventory-details-accordion-header"
                        >
                          <IonLabel className="hk-desktop-digital-inventory-details-accordion-title">
                            Documents
                          </IonLabel>
                        </IonItem>
                        <div
                          slot="content"
                          className="hk-desktop-digital-inventory-details-accordion-content"
                        >
                          <div className="hk-desktop-digital-inventory-details-overview-document-slider">
                            {manualDocuments.map((attr) => {
                              const { id, name, file_url } = attr;
                              return (
                                <div
                                  className="hk-desktop-digital-inventory-details-overview-document-slider-slide"
                                  key={id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    viewDocument(file_url, platformType)
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === 'Enter' ||
                                      event.key === ' '
                                    ) {
                                      event.preventDefault();
                                      viewDocument(file_url, platformType);
                                    }
                                  }}
                                >
                                  <IonImg
                                    src={Documents}
                                    className="hk-desktop-digital-inventory-details-overview-document-slider-slide-img"
                                  />
                                  <IonText
                                    color="medium"
                                    className="hk-desktop-digital-inventory-details-overview-document-slider-slide-name ion-text-center"
                                  >
                                    {name}
                                  </IonText>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </IonAccordion>
                    )}
                  </IonAccordionGroup>
                </div>
              </div>
            </div>
          </div>
        )}
      </IonContent>
      <InventoryDeleteConfirmModal
        isOpen={isDeleteConfirmOpen}
        productName={product?.name || product?.product_template.name}
        productModel={product?.product_template.model}
        productBrand={product?.product_template.manufacturer?.name}
        productType={product?.product_template.type}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
      <InventoryAnalyzeImageModal
        isOpen={isAnalyzeModalOpen}
        photos={ownPhotoAssets}
        loading={isAnalyzing}
        onCancel={() => setIsAnalyzeModalOpen(false)}
        onConfirm={analyzeImage}
      />
    </IonPage>
  );
};

export default InventoryDetailsDesktop;
